import React from "react";

/**
 * EmployeesSection Component
 * Displays a table/list of employees with search functionality
 * Props:
 * - onOpen: Callback function triggered when "Open →" button is clicked
 *           Receives the employee object as parameter
 * - employees: Array of employee objects from API
 * - currentPage: Current page number
 * - totalPages: Total number of pages
 * - onPageChange: Callback to change page
 * - isLoading: Loading state
 *
 * Features:
 * - Employee list with name, email, role
 * - Search input field
 * - Pagination controls (Prev/Next buttons, page numbers)
 * - Checkboxes for bulk selection (placeholder - not yet functional)
 */
export default function EmployeesSection({ onOpen, employees = [], currentPage = 1, totalPages = 1, onPageChange, isLoading }) {
  // Generate initials from name
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '??';
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="cd-employees-section">
      <div className="cd-employees-header">
        <h3 className="cd-employees-title">Employees</h3>
        <div className="cd-search-wrapper">
          <input className="cd-search-input" placeholder="Search" type="text" />
          <span className="cd-search-hint">Ctrl K</span>
        </div>
      </div>

      <div className="cd-employees-table">
        <div className="cd-table-header">
          <div className="cd-th-name">Employee Name</div>
          <div className="cd-th-role">Role</div>
          <div className="cd-th-action">Action</div>
        </div>

        {isLoading && (
          <div className="cd-table-loading">Loading employees...</div>
        )}

        {!isLoading && employees.length === 0 && (
          <div className="cd-table-empty">No employees found.</div>
        )}

        {!isLoading && employees.map((employee) => (
          <div className="cd-table-row" key={employee._id}>
            <div className="cd-employee-info">
              <input type="checkbox" className="cd-checkbox" />
              <div className="cd-avatar">
                {getInitials(employee.firstName, employee.lastName)}
              </div>
              <div className="cd-employee-details">
                <div className="cd-employee-name">
                  {employee.firstName} {employee.lastName}
                </div>
                <div className="cd-employee-email">({employee.email})</div>
              </div>
            </div>
            <div className="cd-employee-role">
              {employee.position?.name || 'Not Set'}
            </div>
            <div className="cd-employee-action">
              <button
                className="cd-open-button"
                onClick={() => onOpen?.({
                  id: employee._id,
                  name: `${employee.firstName} ${employee.lastName}`,
                  email: employee.email,
                  role: employee.position?.name || 'Not Set',
                  position: employee.position,
                  avatar: getInitials(employee.firstName, employee.lastName)
                })}
              >
                Open →
              </button>
            </div>
          </div>
        ))}

        <div className="cd-table-footer">
          <span className="cd-page-info">
            Page {String(currentPage).padStart(2, '0')} Of {String(totalPages).padStart(2, '0')}
          </span>
          <div className="cd-pagination">
            <button
              className="cd-page-btn"
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              ‹ Prev
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                className={`cd-page-number ${page === currentPage ? 'cd-active' : ''}`}
                onClick={() => onPageChange?.(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="cd-page-btn"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
